import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ExcelJS from 'exceljs';
import adminBg from '../Assets/bg/adminbg.jpg';
import Loader from '../Components/Loader';
import useBoholGetYear from '../Hooks/CisHooks/useBoholGetYear';
import useAddCISBohol from '../Hooks/CisHooks/useAddCISBohol';
import useGetYears from '../Hooks/CisHooks/useGetYears';
import useDeleteCIS from '../Hooks/CisHooks/useDeleteCIS';
import useCISBoholLabels from '../Hooks/CisHooks/useCISBoholLabels';
import DeleteConfirmationModal from '../Components/DeleteConfirmationModal';

const CISboholImport = ({ darkMode, onClose, onSave, selectedYear, setSelectedYear, importedFile, setShowFooter }) => {
  const [importedData, setImportedData] = useState([]);
  const [combinedData, setCombinedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { data: existingData, loading: dataLoading, refetch } = useBoholGetYear(selectedYear);
  const { loading: addLoading, success: addSuccess, error: addError } = useAddCISBohol();
  const { years: yearData, loading: yearLoading, error: yearError } = useGetYears();
  const { labels, loading: labelLoading, error: labelError } = useCISBoholLabels();
  const { deleteCIS } = useDeleteCIS();
  const { addCISBohol } = useAddCISBohol();
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorList, setErrorList] = useState([]);
  const labelNames = labels ? labels.map(l => l.label.toLowerCase().trim()) : [];

  const handleSave = async () => {
    console.log("handleSave called");
    setIsLoading(true);
    try {
      const recordsToSave = importedData.filter(item => !item.isLabel);
      if (recordsToSave.length === 0) {
        alert("No records available to save.");
        setIsLoading(false);
        return;
      }

      let failures = [];
      let successes = 0;

      for (const [index, record] of recordsToSave.entries()) {
        const validationError = validateRecord(record);
        if (validationError) {
          failures.push({
            ia_name: record.ia_name || "(No IA name)",
            reason: `Validation error: ${validationError}`,
          });
          continue;
        }

        const formattedRecord = {
          years: Number(record.years) || selectedYear,
          label: record.groupLabel || "",
          ia_name: record.ia_name || "",
          num: record.num || "",
          cis_name: record.cis_name || "",
          districtNum: record.districtNum || "",
          municipality: record.municipality || "",
          mailing_address: record.mailing_address || "",
          president: record.president || "",
          contactNum: record.contactNum || "",
          email_address: record.email_address || "",
          dateOrganized: record.dateOrganized || "",
          sec_RegistrationNum: record.sec_RegistrationNum || "",
          sec_DateApproved: record.sec_DateApproved || "",
          tin: record.tin || "",
          water_permitNum: record.water_permitNum || "",
          water_dateApproved: record.water_dateApproved || "",
          membership_fb: record.membership_fb ? Number(record.membership_fb) : null,
          membership_actual: record.membership_actual ? Number(record.membership_actual) : null,
          sectorNum: record.sectorNum ? Number(record.sectorNum) : null,
          serviceArea: record.serviceArea ? Number(record.serviceArea) : null,
          fusa: record.fusa ? Number(record.fusa) : null,
          operational: record.operational ? Number(record.operational) : null,
          date_moaSigned: record.date_moaSigned || "",
          f_source: record.f_source || "",
          projCost: record.projCost ? Number(record.projCost) : null,
          date_turnOver: record.date_turnOver || "",
          ia_existContract: record.ia_existContract ? Number(record.ia_existContract) : null,
          imt_dateSigned: record.imt_dateSigned || "",
          imt_undercontract: record.imt_undercontract ? Number(record.imt_undercontract) : null,
          canalLength_lined: record.canalLength_lined ? Number(record.canalLength_lined) : null,
          canalLength_unlined: record.canalLength_unlined ? Number(record.canalLength_unlined) : null,
          total_CanalLength: record.total_CanalLength ? Number(record.total_CanalLength) : null,
          fundSource: record.fundSource || "",
          category: record.category || "",
          devolutionPhases: record.devolutionPhases || "",
          idoAssigned: record.idoAssigned || "",
          remarks: record.remarks || "",
        };

        try {
          const { success, error } = await addCISBohol(formattedRecord);
          if (!success) {
            failures.push({
              ia_name: record.ia_name || "(No IA name)",
              reason: error || "Unknown API error",
            });
          } else {
            successes++;
          }
        } catch (err) {
          failures.push({
            ia_name: record.ia_name || "(No IA name)",
            reason: `Exception: ${err.message}`,
          });
        }
      }

      await onSave(recordsToSave);
      await refetch();

      if (successes > 0) {
        alert(successes === recordsToSave.length
          ? "All records saved successfully!"
          : `${successes} of ${recordsToSave.length} records saved successfully.`);
      }
      if (failures.length > 0) {
        setErrorList(failures);
        setErrorModalOpen(true);
      } else {
        onClose(); // Close import UI to return to CISbohol
      }

    } catch (error) {
      console.error("Error in handleSave:", error);
      setErrorList([{ ia_name: "General Error", reason: error.message }]);
      setErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (typeof setShowFooter === 'function') {
      setShowFooter(false);
      return () => setShowFooter(true);
    } else {
      console.warn('setShowFooter is not a function:', setShowFooter);
    }
  }, [setShowFooter]);

  useEffect(() => {
    if (importedFile && existingData && !dataLoading && !labelLoading) {
      const processFile = async () => {
        setIsLoading(true);
        try {
          const workbook = new ExcelJS.Workbook();
          const buffer = await importedFile.arrayBuffer();
          await workbook.xlsx.load(buffer);

          const normalizeSheetName = (name) =>
            (name || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');

          const worksheets = workbook.worksheets || [];
          const cisWorksheet = worksheets.find(
            (ws) => normalizeSheetName(ws.name) === 'cis'
          );
          const cisLikeWorksheet = worksheets.find((ws) =>
            normalizeSheetName(ws.name).startsWith('cis')
          );
          const worksheet = cisWorksheet || cisLikeWorksheet || workbook.getWorksheet(1);

          if (!worksheet) {
            alert('Invalid file format: No worksheet found in the uploaded file.');
            setIsLoading(false);
            return;
          }

          const existingKeys = new Set(
            existingData.map(item => `${item.ia_name}_${item.cis_name}_${item.num}`)
          );

          const newData = [];
          let startDataRow = false;
          let registrationRow = 0;
          let currentLabel = null;

          const getCellText = (value) => {
            if (value === null || value === undefined) return '';
            if (typeof value === 'object') {
              if (value.text) return value.text.toString();
              if (value.result !== undefined && value.result !== null) return value.result.toString();
              if (Array.isArray(value.richText)) {
                return value.richText.map((part) => part.text || '').join('');
              }
            }
            return value.toString();
          };

          worksheet.eachRow((row, rowNumber) => {
            for (let col = 1; col <= 40; col++) {
              const cellValue = getCellText(row.getCell(col).value).toLowerCase();
              if (cellValue.includes('registration no')) {
                registrationRow = rowNumber;
                console.log(`Found "REGISTRATION NO." at row: ${registrationRow}`);
                break;
              }
            }
          });

          worksheet.eachRow((row, rowNumber) => {
            if (rowNumber > registrationRow && registrationRow > 0) {
              startDataRow = true;
            }

            if (registrationRow === 0) {
              startDataRow = true;
            }

            if (startDataRow) {
              const firstCellValueRaw = getCellText(row.getCell(2).value);
              const firstCellValue = firstCellValueRaw.toLowerCase().trim();

              const iaNameRaw = getCellText(row.getCell(3).value);
              const cisNameRaw = getCellText(row.getCell(5).value);

              const hasRowContent = [2, 3, 5, 6, 7, 8, 9, 10, 11, 12, 13].some(
                (col) => getCellText(row.getCell(col).value).trim() !== ''
              );
              const looksLikeHeader =
                firstCellValue === 'no' ||
                iaNameRaw.toLowerCase().includes('name of ia') ||
                cisNameRaw.toLowerCase().includes('name of cis');

              if (!hasRowContent || looksLikeHeader) {
                return;
              }

              if (
                firstCellValue.includes('subtotal') ||
                firstCellValue.includes('grand total')
              ) {
                return;
              }

              const isLabel = labelNames.includes(firstCellValue);
              if (isLabel) {
                currentLabel = firstCellValue;
              }

              const rowData = {
                num: firstCellValueRaw,
                ia_name: iaNameRaw,
                cis_name: cisNameRaw,
                districtNum: getCellText(row.getCell(6).value),
                municipality: getCellText(row.getCell(7).value),
                mailing_address: getCellText(row.getCell(8).value),
                president: getCellText(row.getCell(9).value),
                contactNum: getCellText(row.getCell(10).value),
                email_address: getCellText(row.getCell(11).value),
                dateOrganized: getCellText(row.getCell(12).value),
                sec_RegistrationNum: getCellText(row.getCell(13).value),
                sec_DateApproved: getCellText(row.getCell(14).value),
                tin: getCellText(row.getCell(15).value),
                water_permitNum: getCellText(row.getCell(16).value),
                water_dateApproved: getCellText(row.getCell(17).value),
                membership_fb: row.getCell(18).value ? Number(row.getCell(18).value.toString().replace(/,/g, '')) : null,
                membership_actual: row.getCell(19).value ? Number(row.getCell(19).value.toString().replace(/,/g, '')) : null,
                sectorNum: row.getCell(20).value ? Number(row.getCell(20).value.toString().replace(/,/g, '')) : null,
                serviceArea: row.getCell(21).value ? Number(row.getCell(21).value.toString().replace(/,/g, '')) : null,
                fusa: row.getCell(22).value ? Number(row.getCell(22).value.toString().replace(/,/g, '')) : null,
                operational: row.getCell(23).value ? Number(row.getCell(23).value.toString().replace(/,/g, '')) : null,
                date_moaSigned: getCellText(row.getCell(24).value),
                f_source: getCellText(row.getCell(25).value),
                projCost: row.getCell(26).value ? Number(row.getCell(26).value.toString().replace(/,/g, '')) : null,
                date_turnOver: getCellText(row.getCell(27).value),
                ia_existContract: row.getCell(28).value ? Number(row.getCell(28).value.toString().replace(/,/g, '')) : null,
                imt_dateSigned: getCellText(row.getCell(29).value),
                imt_undercontract: row.getCell(30).value ? Number(row.getCell(30).value.toString().replace(/,/g, '')) : null,
                canalLength_lined: row.getCell(31).value ? Number(row.getCell(31).value.toString().replace(/,/g, '')) : null,
                canalLength_unlined: row.getCell(32).value ? Number(row.getCell(32).value.toString().replace(/,/g, '')) : null,
                total_CanalLength: row.getCell(33).value ? Number(row.getCell(33).value.toString().replace(/,/g, '')) : null,
                fundSource: getCellText(row.getCell(34).value),
                category: getCellText(row.getCell(35).value),
                devolutionPhases: getCellText(row.getCell(36).value),
                idoAssigned: getCellText(row.getCell(37).value),
                remarks: getCellText(row.getCell(38).value),
                years: selectedYear?.toString() || '',
                isLabel: isLabel,
                isImported: true,
                groupLabel: isLabel ? firstCellValue : currentLabel
              };

              rowData.isDuplicate = existingKeys.has(
                `${rowData.ia_name}_${rowData.cis_name}_${rowData.num}`
              );
              newData.push(rowData);
            }
          });

          if (newData.length === 0) {
            alert('No valid data to import.');
            setIsLoading(false);
            return;
          }

          setImportedData(newData);
        } catch (err) {
          alert('Failed to import data. Please check the file format.');
        } finally {
          setIsLoading(false);
        }
      };

      processFile();
    }
  }, [importedFile, existingData, dataLoading, labelLoading, labels, selectedYear]);

  useEffect(() => {
    const combinedDataByLabel = [];

    const existingDataWithLabels = existingData.map((item, index, array) => {
      const isLabel = labelNames.includes(item.label?.toLowerCase().trim());
      let groupLabel = item.label?.toLowerCase().trim();
      if (!isLabel && index > 0) {
        for (let i = index - 1; i >= 0; i--) {
          if (labelNames.includes(array[i].label?.toLowerCase().trim())) {
            groupLabel = array[i].label.toLowerCase().trim();
            break;
          }
        }
      }
      return { ...item, isImported: false, isLabel, groupLabel: groupLabel || null };
    });

    labelNames.forEach(label => {
      const labelRow = importedData.find(item => item.isLabel && item.num.toLowerCase().trim() === label) ||
                       existingDataWithLabels.find(item => item.isLabel && item.label?.toLowerCase().trim() === label) ||
                       { num: label, ia_name: label, cis_name: label, isLabel: true, isImported: false, groupLabel: label };

      const existingRows = existingDataWithLabels.filter(item => !item.isLabel && item.groupLabel === label);
      const importedRows = importedData.filter(item => !item.isLabel && item.groupLabel === label);

      if (existingRows.length > 0 || importedRows.length > 0) {
        combinedDataByLabel.push(labelRow, ...existingRows, ...importedRows);
      }
    });

    const ungroupedExisting = existingDataWithLabels.filter(item => !item.groupLabel && !item.isLabel);
    const ungroupedImported = importedData.filter(
      item => !item.isLabel && (!item.groupLabel || !labelNames.includes(item.groupLabel))
    );

    if (ungroupedExisting.length > 0 || ungroupedImported.length > 0) {
      combinedDataByLabel.push(
        { num: 'Ungrouped', ia_name: 'Ungrouped', cis_name: 'Ungrouped', isLabel: true, isImported: false, groupLabel: 'ungrouped' },
        ...ungroupedExisting,
        ...ungroupedImported
      );
    }

    setCombinedData(combinedDataByLabel);
  }, [existingData, importedData, labelNames]);

  const validateRecord = (record) => {
    const requiredFields = ['ia_name', 'num', 'cis_name', 'years'];
    const numericFields = [
      'membership_fb', 'membership_actual', 'sectorNum', 'serviceArea', 'fusa',
      'operational', 'projCost', 'ia_existContract', 'imt_undercontract',
      'canalLength_lined', 'canalLength_unlined', 'total_CanalLength'
    ];
    const dateFields = [
      'dateOrganized', 'sec_DateApproved', 'water_dateApproved',
      'date_moaSigned', 'date_turnOver', 'imt_dateSigned'
    ];

    for (const field of requiredFields) {
      if (!record[field] && record[field] !== 0) {
        return `Missing required field: ${field}`;
      }
    }

    for (const field of numericFields) {
      if (record[field] !== null && isNaN(Number(record[field]))) {
        return `Invalid numeric value for ${field}: ${record[field]}`;
      }
    }

    for (const field of dateFields) {
      if (record[field] && !/^\d{4}-\d{2}-\d{2}$/.test(record[field])) {
        return `Invalid date format for ${field}: ${record[field]} (expected YYYY-MM-DD)`;
      }
    }

    if (isNaN(Number(record.years))) {
      return `Invalid value for years: ${record.years} (expected a number)`;
    }

    return null;
  };

  const handleDeleteClick = (index) => {
    const item = combinedData[index];
    setConfirmDelete({ item, index });
  };

  const handleConfirmDelete = async () => {
    const { item, index } = confirmDelete;
    try {
      if (item.id && !item.isImported) {
        await deleteCIS({ variables: { id: item.id } });
        setCombinedData(prev => prev.filter((_, i) => i !== index));
      } else if (item.isImported) {
        setImportedData(prev => prev.filter((_, i) => 
          `${prev[i].ia_name}_${prev[i].cis_name}_${prev[i].num}` !== 
          `${item.ia_name}_${item.cis_name}_${item.num}`
        ));
      }
    } catch (err) {
      alert('Failed to delete record.');
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleDiscard = () => {
    setImportedData([]);
    const existingDataWithLabels = existingData.map((item, index, array) => {
      const isLabel = labelNames.includes(item.label?.toLowerCase().trim());
      let groupLabel = item.label?.toLowerCase().trim();
      if (!isLabel && index > 0) {
        for (let i = index - 1; i >= 0; i--) {
          if (labelNames.includes(array[i].label?.toLowerCase().trim())) {
            groupLabel = array[i].label.toLowerCase().trim();
            break;
          }
        }
      }
      return { ...item, isImported: false, isLabel, groupLabel: groupLabel || null };
    });

    const combinedDataByLabel = [];
    labelNames.forEach(label => {
      const labelRow = existingDataWithLabels.find(item => item.isLabel && item.label?.toLowerCase().trim() === label) ||
                       { num: label, ia_name: label, cis_name: label, isLabel: true, isImported: false, groupLabel: label };
      const existingRows = existingDataWithLabels.filter(item => !item.isLabel && item.groupLabel === label);
      if (existingRows.length > 0) {
        combinedDataByLabel.push(labelRow, ...existingRows);
      }
    });

    const ungroupedExisting = existingDataWithLabels.filter(item => !item.groupLabel && !item.isLabel);
    if (ungroupedExisting.length > 0) {
      combinedDataByLabel.push(
        { num: 'Ungrouped', ia_name: 'Ungrouped', cis_name: 'Ungrouped', isLabel: true, isImported: false, groupLabel: 'ungrouped' },
        ...ungroupedExisting
      );
    }

    setCombinedData(combinedDataByLabel);
    onClose();
  };

  if (isLoading || dataLoading || addLoading || yearLoading || labelLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: darkMode ? 'black' : 'white',
        }}
      >
        <Loader />
      </div>
    );
  }

  if (yearError || addError || labelError) {
    return (
      <div style={{ padding: '20px', color: darkMode ? '#fff' : '#333' }}>
        <p>Error: {yearError?.message || addError?.message || labelError || 'An unknown error occurred'}</p>
        <StyledButton onClick={() => refetch()}>
          Retry
        </StyledButton>
      </div>
    );
  }

  return (
    <>
      <Container darkMode={darkMode}>
        {addSuccess && <SuccessMessage>{addSuccess}</SuccessMessage>}
        {addError && <ErrorMessage>{addError}</ErrorMessage>}
        <HeaderContainer>
          <RightControls>
            <StyledButton onClick={handleDiscard}>Discard</StyledButton>
            <StyledButton onClick={handleSave} disabled={addLoading || importedData.length === 0}>
              {addLoading ? 'Saving...' : 'Save'}
            </StyledButton>
          </RightControls>
        </HeaderContainer>
        <ColorLegend darkMode={darkMode}>
          <LegendItem color="#ffeb99">New Data</LegendItem>
          <LegendItem color="#ffcccc">Duplicate Data</LegendItem>
          <LegendItem color="#a3c9a3">Label</LegendItem>
        </ColorLegend>
        <p
          style={{
            padding: '10px 40px',
            margin: 0,
            fontSize: '14px',
            color: darkMode ? '#ccc' : '#555',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          Preview of the data from the imported file. New records are highlighted in yellow, duplicates in red, and labels in green.
        </p>
        <TableContainer>
          {combinedData.length === 0 ? (
            <NoDataMessage darkMode={darkMode}>
              No data to display. Please check the file and try again.
            </NoDataMessage>
          ) : (
            <TableWrapper id="tableWrapper">
              <div id="scrollContent" style={{ width: '100%', overflowX: 'auto' }}>
                <Table darkMode={darkMode}>
                  <thead>
                    <tr>
                      <TableHeader darkMode={darkMode}>ACTIONS</TableHeader>
                      <TableHeader darkMode={darkMode}>NO</TableHeader>
                      <TableHeader darkMode={darkMode}>NAME OF IA</TableHeader>
                      <TableHeader darkMode={darkMode}>NO</TableHeader>
                      <TableHeader darkMode={darkMode}>NAME OF CIS</TableHeader>
                      <TableHeader darkMode={darkMode}>DISTRICT NO.</TableHeader>
                      <TableHeader darkMode={darkMode}>MUNICIPALITY</TableHeader>
                      <TableHeader darkMode={darkMode}>MAILING ADDRESS</TableHeader>
                      <TableHeader darkMode={darkMode}>NAME OF PRESIDENT</TableHeader>
                      <TableHeader darkMode={darkMode}>CONTACT NUMBER</TableHeader>
                      <TableHeader darkMode={darkMode}>EMAIL ADDRESS</TableHeader>
                      <TableHeader darkMode={darkMode}>DATE IA ORGANIZED</TableHeader>
                      <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                        SEC REGISTRATION
                      </TableHeader>
                      <TableHeader darkMode={darkMode}>TAX IDENTIFICATION NUMBER (TIN)</TableHeader>
                      <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                        WATER PERMIT
                      </TableHeader>
                      <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                        MEMBERSHIP
                      </TableHeader>
                      <TableHeader darkMode={darkMode}>sectorNum</TableHeader>
                      <TableHeader darkMode={darkMode}>serviceArea</TableHeader>
                      <TableHeader darkMode={darkMode}>fusa</TableHeader>
                      <TableHeader darkMode={darkMode}>operational</TableHeader>
                      <TableHeader darkMode={darkMode} colSpan="4" style={{ textAlign: 'center' }}>
                        ORIGINAL CONSTRUCTION
                      </TableHeader>
                      <TableHeader darkMode={darkMode}>ia_existContract</TableHeader>
                      <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                        IMT CONTRACT
                      </TableHeader>
                      <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                        CANAL LENGTH
                      </TableHeader>
                      <TableHeader darkMode={darkMode}>total_CanalLength</TableHeader>
                      <TableHeader darkMode={darkMode}>FUNDSOURCE</TableHeader>
                      <TableHeader darkMode={darkMode}>CATEGORY</TableHeader>
                      <TableHeader darkMode={darkMode}>PHASES FOR DEVOLUTION</TableHeader>
                      <TableHeader darkMode={darkMode}>IDO ASSIGNED</TableHeader>
                      <TableHeader darkMode={darkMode}>REMARKS</TableHeader>
                    </tr>
                    <tr>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>REGISTRATION NO.</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>DATE APPROVED</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>PERMIT NO.</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>DATE APPROVED</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>membership_fb</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>membership_actual</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>DATE OF MOA SIGNED</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>F-SOURCE</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>projCost</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>DATE OF TURNOVER</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>DATE OF SIGNING</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>imt_undercontract</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>canalLength_lined</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}>canalLength_unlined</TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                      <TableHeaderS darkMode={darkMode}></TableHeaderS>
                    </tr>
                  </thead>
                  <tbody>
                    {combinedData.map((item, index) => {
                      const isFirstIA = index === 0 || combinedData[index - 1].ia_name !== item.ia_name;
                      return item.isLabel ? (
                        <TableRow key={`label-${index}`} isLabel={true} darkMode={darkMode}>
                          <TableData
                            colSpan={39}
                            isLabel={true}
                            darkMode={darkMode}
                            style={{ fontWeight: 'bold', textAlign: 'left', backgroundColor: '#a3c9a3' }}
                          >
                            {item.num}
                          </TableData>
                        </TableRow>
                      ) : (
                        <TableRow
                          key={`${item.id || `row-${index}`}-${index}`}
                          darkMode={darkMode}
                          isDuplicate={item.isImported && item.isDuplicate}
                          isNew={item.isImported && !item.isDuplicate}
                          isExisting={!item.isImported}
                        >
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px', padding: '8px 0' }}>
                            <ButtonGroup>
                              <StyledButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(index);
                                }}
                              >
                                ❌
                              </StyledButton>
                            </ButtonGroup>
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                            {item.num || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '240px' }}>
                            {isFirstIA ? item.ia_name : ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                            {item.num || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px' }}>
                            {item.cis_name || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                            {item.districtNum || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.municipality || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.mailing_address || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.president || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '150px' }}>
                            {item.contactNum || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '200px', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.email_address || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                            {item.dateOrganized || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px', maxWidth: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.sec_RegistrationNum || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px', maxWidth: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.sec_DateApproved || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px', maxWidth: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.tin || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                            {item.water_permitNum || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                            {item.water_dateApproved || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                            {item.membership_fb ? Number(item.membership_fb).toFixed(3) : ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                            {item.membership_actual ? Number(item.membership_actual).toFixed(3) : ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                            {item.sectorNum ? Number(item.sectorNum).toFixed(3) : ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                            {item.serviceArea ? Number(item.serviceArea).toFixed(3) : ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                            {item.fusa ? Number(item.fusa).toFixed(3) : ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                            {item.operational ? Number(item.operational).toFixed(3) : ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                            {item.date_moaSigned || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                            {item.f_source || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                            {item.projCost ? Number(item.projCost).toFixed(3) : ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                            {item.date_turnOver || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                            {item.ia_existContract ? Number(item.ia_existContract).toFixed(3) : ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                            {item.imt_dateSigned || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                            {item.imt_undercontract ? Number(item.imt_undercontract).toFixed(3) : ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                            {item.canalLength_lined ? Number(item.canalLength_lined).toFixed(3) : ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                            {item.canalLength_unlined ? Number(item.canalLength_unlined).toFixed(3) : ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                            {item.total_CanalLength ? Number(item.total_CanalLength).toFixed(3) : ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                            {item.fundSource || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                            {item.category || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                            {item.devolutionPhases || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                            {item.idoAssigned || ''}
                          </TableData>
                          <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                            {item.remarks || ''}
                          </TableData>
                        </TableRow>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </TableWrapper>
          )}
        </TableContainer>
        {errorModalOpen && (
          <ErrorModal>
            <ErrorModalContent darkMode={darkMode}>
              <h3>Errors Encountered During Save</h3>
              <ul>
                {errorList.map((error, index) => (
                  <li key={index}>
                    {error.ia_name}: {error.reason}
                  </li>
                ))}
              </ul>
              <StyledButton onClick={() => { setErrorModalOpen(false); onClose(); }}>
                Close
              </StyledButton>
            </ErrorModalContent>
          </ErrorModal>
        )}
      </Container>
      {confirmDelete && (
        <DeleteConfirmationModal
          isOpen={!!confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onConfirm={handleConfirmDelete}
          itemName={confirmDelete.item.ia_name || 'this item'}
          darkMode={darkMode}
        />
      )}
    </>
  );
};

export default CISboholImport;

// Styled Components
const Container = styled.div`
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#f2f2f2')};
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  color: ${({ darkMode }) => (darkMode ? '#757272' : '#333')};
  padding: 80px 20px 20px;
  width: 100%;
  min-height: 100vh;
  overflow-y: auto;
  box-sizing: border-box;
  position: relative;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0 20px;
  margin-top: -40px;
`;

const RightControls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
`;

const SuccessMessage = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: #4caf50;
  color: #fff;
  padding: 10px 20px;
  border-radius: 5px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  z-index: 9999;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: #f44336;
  color: #fff;
  padding: 10px 20px;
  border-radius: 5px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  z-index: 9999;
  font-size: 14px;
`;

const ErrorModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ErrorModalContent = styled.div`
  background: ${({ darkMode }) => (darkMode ? '#333' : '#fff')};
  padding: 20px;
  border-radius: 5px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};

  h3 {
    margin-top: 0;
    color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
  }

  ul {
    list-style: none;
    padding: 0;
    margin-bottom: 20px;
  }

  li {
    padding: 5px 0;
    color: ${({ darkMode }) => (darkMode ? '#ff9999' : '#d32f2f')};
  }
`;

const StyledButton = styled.button`
  margin: 0 2px;
  padding: 6px 10px;
  background-color: #eee;
  border: 1px solid #aaa;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 14px;

  &:hover {
    background-color: #ddd;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-top: 10px;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  margin-top: 10px;
  position: relative;
  background-color: ${({ darkMode }) => (darkMode ? 'rgb(42, 48, 42)' : '#f2f2f2')};

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 2500px;
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
`;

const TableHeader = styled.th`
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#2e7d32')};
  color: ${({ darkMode }) => (darkMode ? '#333' : '#fff')};
  padding: 10px;
  font-size: 14px;
  border: 1px solid #ddd;
  text-align: center;
  vertical-align: middle;
`;

const TableHeaderS = styled.th`
  position: sticky;
  top: 39px;
  z-index: 1;
  background-color: ${({ darkMode }) => (darkMode ? '#1b5e20' : '#4caf50')};
  color: #ffffff;
  padding: 0 10px;
  border: 1px solid #ddd;
  font-size: 12px;
  text-align: center;
`;

const TableRow = styled.tr`
  background: ${({ isLabel, darkMode, isDuplicate, isNew, isExisting }) =>
    isLabel
      ? darkMode ? '#4a704a' : '#a3c9a3'
      : isDuplicate
      ? '#ffcccc'
      : isNew
      ? '#ffeb99'
      : isExisting
      ? darkMode ? 'rgba(3, 82, 33, 0.3)' : '#fff'
      : darkMode
      ? 'rgba(3, 82, 33, 0.3)'
      : '#fff'};
`;

const TableData = styled.td`
  padding: 5px;
  font-size: 12px;
  border: 1px solid #ddd;
  color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#333')};
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 20px;
  font-size: 16px;
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 5px;
  justify-content: center;
`;

const ColorLegend = styled.div`
  padding: 10px 40px;
  display: flex;
  gap: 20px;
  align-items: center;
  background-color: ${({ darkMode }) => (darkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.8)')};
  border-bottom: 1px solid ${({ darkMode }) => (darkMode ? '#555' : '#ddd')};
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};

  &:before {
    content: '';
    width: 15px;
    height: 15px;
    background-color: ${({ color }) => color};
    margin-right: 5px;
    border: 1px solid ${({ darkMode }) => (darkMode ? '#666' : '#ccc')};
  }
`;